import { ScrollStrategy } from '@angular/cdk/overlay';

/**
 * Freezes the page behind an overlay by locking whichever element actually
 * scrolls, without moving it.
 *
 * Why not the CDK's own `BlockScrollStrategy`: `styles.scss` sets
 * `html, body { height: 100%; overflow-x: hidden }`, which makes <body> - not
 * the viewport - the element that scrolls. The stock strategy only knows how
 * to freeze the viewport, so on a scrolled page it:
 *   - pins <html> at `position: fixed; top: -scrollTop` while <body> keeps its
 *     own scrollTop, shifting the page up by the scroll offset twice over and
 *     leaving a blank area below the content (the mobile "white screen");
 *   - adds `overflow-y: scroll` to <html>, claiming a scrollbar gutter the
 *     page did not have before (the desktop "jitter");
 *   - restores with `window.scroll()` on close, which never reaches the <body>
 *     scroller, so the page does not come back to where it was.
 *
 * `overflow-y: hidden` on the real scroller needs no offsets at all: the
 * scroll position is simply held in place, so nothing can move on open or on
 * close.
 */
interface LockedElement {
  el: HTMLElement;
  overflowY: string;
  scrollbarGutter: string;
}

let lockDepth = 0;
let lockedElements: LockedElement[] = [];

/**
 * The elements that can scroll the page right now. Both <body> and <html> are
 * checked rather than assumed so this keeps working if the global overflow
 * rules change; when neither scrolls, <html> is locked anyway so that content
 * growing while the overlay is open cannot start scrolling behind it.
 */
function scrollLockTargets(doc: Document): HTMLElement[] {
  const targets = [doc.body, doc.documentElement].filter(
    (el) => el.scrollHeight > el.clientHeight && isScrollContainer(el)
  );

  return targets.length > 0 ? targets : [doc.documentElement];
}

function isScrollContainer(el: HTMLElement): boolean {
  const overflowY = getComputedStyle(el).overflowY;

  return overflowY === 'auto' || overflowY === 'scroll';
}

/**
 * Overlays stack (the sign-up gate opens on top of the purchase flow), so the
 * lock is reference counted: the page unfreezes when the last one closes,
 * whatever order they close in.
 */
export function acquirePageScrollLock(doc: Document): void {
  if (lockDepth++ > 0) {
    return;
  }

  lockedElements = scrollLockTargets(doc).map((el) => {
    const locked: LockedElement = {
      el,
      overflowY: el.style.overflowY,
      scrollbarGutter: el.style.getPropertyValue('scrollbar-gutter'),
    };

    // Hiding a scrollbar that is currently taking up space would reflow the
    // page a few pixels wider; keep its gutter reserved instead.
    if (el.offsetWidth > el.clientWidth) {
      el.style.setProperty('scrollbar-gutter', 'stable');
    }
    el.style.overflowY = 'hidden';

    return locked;
  });
}

export function releasePageScrollLock(): void {
  if (lockDepth === 0 || --lockDepth > 0) {
    return;
  }

  for (const locked of lockedElements) {
    locked.el.style.overflowY = locked.overflowY;
    if (locked.scrollbarGutter) {
      locked.el.style.setProperty('scrollbar-gutter', locked.scrollbarGutter);
    } else {
      locked.el.style.removeProperty('scrollbar-gutter');
    }
  }
  lockedElements = [];
}

export class PageScrollLockStrategy implements ScrollStrategy {
  private isEnabled = false;

  constructor(private document: Document) {}

  public attach(): void {}

  public enable(): void {
    if (this.isEnabled) {
      return;
    }
    this.isEnabled = true;
    acquirePageScrollLock(this.document);
  }

  public disable(): void {
    if (!this.isEnabled) {
      return;
    }
    this.isEnabled = false;
    releasePageScrollLock();
  }
}
